import { clsx } from 'clsx';
import type { FC, MouseEvent, MouseEventHandler, PropsWithChildren } from 'react';
import { Icons } from '../../icons/Icon';
import styles from './button.module.css';

type ButtonProps = {
  id?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  className?: string;
  onClick?: MouseEventHandler;
  type?: 'contained' | 'underline' | 'outlined';
  icon?: string;
  href?: string;
  color?: string;
  style?: React.CSSProperties;
  iconSize?: number;
  title?: string;
  dataTestId?: string;
} & PropsWithChildren;

/**
 * @param ButtonProps
 * @returns
 */
export const Button: FC<ButtonProps> = ({
  id = undefined,
  isDisabled = false,
  onClick,
  children,
  href,
  icon = '',
  type = 'contained',
  isLoading = false,
  className,
  color = '#fff',
  style,
  iconSize = null,
  title = '',
  dataTestId = '',
}) => {
  const getStyles = () => {
    let className = '';

    switch (type) {
      case 'contained':
        className = `${styles.contained} ${styles.buttonBase}`;
        break;
      case 'underline':
        className = `${styles.underline} ${styles.buttonBase}`;
        break;
      case 'outlined':
        className = `${styles.outlined} ${styles.buttonBase}`;
        break;
      default:
        className = '';
    }

    return clsx(className, { [styles.disabled]: isDisabled });
  };

  const iconButtonClassName = clsx({
    [styles.iconButton]: true,
    [styles.disabled]: isDisabled,
  });

  const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    event.preventDefault();
    if (!isDisabled && onClick) {
      onClick(event);
    }
  };
  if (icon && !children) {
    return (
      <button
        id={id}
        title={title}
        data-testid={dataTestId}
        style={style}
        onClick={(event) => handleClick(event)}
        disabled={isDisabled}
        className={`${iconButtonClassName} ${className}`}
      >
        {
          <Icons
            icon={icon}
            color={color}
            size={iconSize ? iconSize : 16}
            customStyle={isDisabled ? { opacity: '0.5' } : {}}
          />
        }
      </button>
    );
  }
  if (href) {
    return (
      <a href={href} data-testid={dataTestId} className={getStyles()} style={style}>
        {icon && !isLoading && <span className={styles.buttonIcon}>{<Icons size={14} icon={icon} />}</span>}
        {isLoading && <span className={styles.buttonIcon}>{<Icons size={14} icon={'Processing'} />}</span>}
        <p className={styles.buttonText}>{children}</p>
      </a>
    );
  } else {
    return (
      <button
        id={id}
        data-testid={dataTestId}
        title={title}
        disabled={isDisabled}
        style={style}
        className={getStyles()}
        onClick={handleClick}
      >
        {icon && !isLoading && <span className={styles.buttonIcon}>{<Icons size={14} icon={icon} />}</span>}
        {isLoading && <span className={styles.buttonIcon}>{<Icons size={14} icon={'Processing'} />}</span>}
        <p className={styles.buttonText}>{children}</p>
      </button>
    );
  }
};
